import { useState, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { Button } from '../../../components/buttons'
import { intl } from '../../../../i18n'

type Props = {
  open: boolean
  rawToken: string | null
  onClose: () => void
}

const TokenDisplayModal = ({ open, rawToken, onClose }: Props) => {
  const [copied, setCopied] = useState(false)

  const onCopy = useCallback(async () => {
    if (!rawToken) return
    try {
      await navigator.clipboard.writeText(rawToken)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy token:', error)
    }
  }, [rawToken])

  const onCloseDialog = useCallback(() => {
    setCopied(false)
    onClose()
  }, [onClose])

  return (
    <Dialog open={open} onClose={onCloseDialog} maxWidth="md" fullWidth={true}>
      <DialogTitle>
        {intl.formatMessage({
          id: 'userApiToken.tokenDisplayModal.title',
        })}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          {intl.formatMessage({
            id: 'userApiToken.tokenDisplayModal.warning',
          })}
        </Alert>
        <TextField
          fullWidth={true}
          variant="outlined"
          value={rawToken || ''}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={intl.formatMessage({
                    id: copied
                      ? 'userApiToken.tokenDisplayModal.copied'
                      : 'userApiToken.tokenDisplayModal.copyToClipboard',
                  })}
                  placement="top"
                >
                  <IconButton onClick={onCopy} edge="end">
                    {copied ? (
                      <CheckIcon color="success" />
                    ) : (
                      <ContentCopyIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            sx: {
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button
          variant="outlined"
          colorPattern="monotone"
          onClick={onCloseDialog}
        >
          {intl.formatMessage({ id: 'dialog.close' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TokenDisplayModal
